import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SubscriptionStateService } from '../services/subscription-state.service';

/**
 * Turns the API's subscription refusals into something the whole app reacts to
 * consistently, instead of leaving every screen to render its own confusing
 * failure.
 *
 * The API gained a server-side subscription gate: an EXPIRED, GRACE_PERIOD,
 * CANCELLED or SUSPENDED tenant may still READ everything, but every write is
 * refused before it reaches a controller. Reads staying open is what makes this
 * interceptor's job narrow — nothing here has to guess, because the two refusals
 * are distinct and both carry a stable code:
 *
 *   402 Payment Required  · SUBSCRIPTION_INACTIVE — fixed by paying. Route them
 *                           to Billing.
 *   403 Forbidden         · TENANT_SUSPENDED      — NOT fixed by paying. Never
 *                           show a "renew" call to action for it.
 *
 * Registered AFTER jwtInterceptor so a 401 refresh is attempted first: a request
 * that fails for a stale token must not be reported as a billing problem.
 */
export const subscriptionInterceptor: HttpInterceptorFn = (req, next) => {
  const state  = inject(SubscriptionStateService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const code = (error.error as { errorCode?: string } | undefined)?.errorCode;

      // Matched on the status AND the code together. A bare 402 from some other
      // source, or a 403 from ordinary role-based authorization, is not a
      // subscription problem and must fall through untouched — otherwise every
      // permission denial in the product would start advertising an upgrade.
      const isSubscriptionBlock = error.status === 402 && code === 'SUBSCRIPTION_INACTIVE';
      const isTenantSuspended   = error.status === 403 && code === 'TENANT_SUSPENDED';

      if (isSubscriptionBlock || isTenantSuspended) {
        const body = error.error as {
          message?: string;
          data?: { planStatus?: string; tenantStatus?: string; graceEndDate?: string; accessRestricted?: boolean };
        } | undefined;

        state.reportBlocked({
          reason      : isTenantSuspended ? 'TENANT_SUSPENDED' : 'SUBSCRIPTION_INACTIVE',
          message     : body?.message ?? 'Your subscription is not active.',
          planStatus  : body?.data?.planStatus,
          tenantStatus: body?.data?.tenantStatus,
          graceEndDate: body?.data?.graceEndDate,
          accessRestricted: body?.data?.accessRestricted === true
        });

        // Only a lapsed SUBSCRIPTION is worth redirecting: the billing page is
        // where the tenant fixes it. A suspension is not something they can act
        // on there, so they stay where they are and read the banner.
        //
        // Guarded against redirecting away from Billing itself, which would trap
        // a tenant in a loop the moment a background poll on that page failed.
        if (isSubscriptionBlock && !router.url.startsWith('/app/tenant/billing'))
          router.navigate(['/app/tenant/billing']);
      }

      return throwError(() => error);
    })
  );
};
